import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Tab, Tabs, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FileViewer from './FileViewer';
import { toast } from 'react-toastify';
import {
  faEllipsisVertical,
  faTrash,
  faBoxOpen,
  faFile,
  faImage,
  faFilePdf,
  faFileWord,
  faFileExcel,
  faDownload,
  faEye,
  faEyeSlash,
  faEdit
} from '@fortawesome/free-solid-svg-icons';
import FileUploader from './FileUploader';
import { 
  uploadFile, 
  setFilePublic, 
  fileToTrash, 
  fileRestoreTrash, 
  fileRemove,
  getFile,
  renameFile,
  getDictionary,
  getDocInfo // Добавлен импорт
} from './actions';

const DocumentModal = ({ isOpen, onClose, documentData, onSave, onDocumentsUpdate, typeDoc }) => { // Добавлен пропс onDocumentsUpdate
  const [activeTab, setActiveTab] = useState('details');
  const [filesTab, setFilesTab] = useState('active');
  const [activeFileMenu, setActiveFileMenu] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    type: typeDoc?.value,
    description: typeDoc?.description
  });
  const [files, setFiles] = useState([]);
  const [dictionary, setDictionary] = useState([]);
  const [loading, setLoading] = useState(false);

  const [editingFileId, setEditingFileId] = useState(null);
  const [editingFileName, setEditingFileName] = useState('');

  const [viewingFile, setViewingFile] = useState(null);
  const [isViewerOpen, setIsViewerOpen] = useState(false);


  useEffect(() => {
    if (documentData) {
      setFormData({
        name: documentData.name || '',
        type: documentData.type || '',
        description: documentData.description || ''
      });
      loadDocumentFiles(); // Загружаем файлы при открытии модалки
    } else {
      setFormData({
        name: '',
        type: typeDoc?.value,
        description: typeDoc?.description
      });
      setFiles([]);
    }
  }, [documentData, isOpen]);

  // Функция загрузки файлов документа
  const loadDocumentFiles = async () => {
    if (!documentData?.id) return;
    
    try {
      setLoading(true);
      const response = await getDocInfo(documentData.id);
      if (response.data) {
        const transformedFiles = transformFiles(response.data.files || []);
        setFiles(transformedFiles);
      }
    } catch (error) {
      console.error('Error loading document files:', error);
      //toast.error('Помилка завантаження файлів');
    } finally {
      setLoading(false);
    }
  };

  const handleRenameFile = async (file) => {
  // Сохраняем оригинальное расширение файла
  const fileExtension = file.name.split('.').pop();
  const fileNameWithoutExtension = file.name.substring(0, file.name.lastIndexOf('.'));
  
  const newName = prompt('Введіть нову назву файлу:', fileNameWithoutExtension);
  if (newName && newName !== fileNameWithoutExtension) {
    // Добавляем расширение к новому имени
    const newFileName = `${newName}.${fileExtension}`;
    
    try {
      await renameFile(documentData.id, file.id, newFileName);
      //toast.success('Файл перейменовано');
      await loadDocumentFiles(); // Перезагружаем файлы
    } catch (error) {
      console.error('Error renaming file:', error);
      //toast.error('Помилка перейменування файлу');
    }
  }
};


  const transformFiles = (files) => {
    if (!files || !files.length) return [];
    
    return files
      .filter(file => !file.status?.removed)
      .map(file => ({
        id: file._id,
        name: file.name,
        type: getFileType(file.mtype),
        size: formatFileSize(file.length),
        uploadedAt: formatDate(file.date_upload || file.date),
        status: file.status?.trash ? 'trash' : 'active',
        is_public: file.is_public,
        mtype: file.mtype
      }));
  };

  const getFileType = (mtype) => {
    if (mtype?.includes('image')) return 'image';
    if (mtype?.includes('pdf')) return 'pdf';
    if (mtype?.includes('word') || mtype?.includes('doc')) return 'docx';
    if (mtype?.includes('excel') || mtype?.includes('sheet')) return 'xlsx';
    return 'file';
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 Б';
    if (bytes < 1024) return `${bytes} Б`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} КБ`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('uk-UA');
  };

  useEffect(()=>{
    async function dictionarySet() {
      const ret = await getDictionary();
      setDictionary(ret.map(r=>({value: r.name, description: r.description})));
    }
    dictionarySet();
  },[isOpen])

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await onSave(formData);
      if (onDocumentsUpdate) {
        onDocumentsUpdate(); // Обновляем список документов
      }
      onClose();
    } catch (error) {
      console.error('Error saving document:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileUpload = async (selectedFiles) => {
    if (!documentData) {
      //toast.error('Спочатку збережіть документ');
      return;
    }

    try {
      setLoading(true);
      for (const file of selectedFiles) {
        const fileInfo = {
          doc_id: documentData.id,
          name: file.name,
          mtype: file.type,
          length: file.size,
          date: new Date().toISOString()
        };

        const fileBuffer = await readFileAsBuffer(file);
        await uploadFile(fileInfo, fileBuffer);
      }
      
      //toast.success('Файли успішно завантажено');
      await loadDocumentFiles(); // Перезагружаем файлы после загрузки
    } catch (error) {
      console.error('Error uploading files:', error);
      //toast.error('Помилка завантаження файлів');
    } finally {
      setLoading(false);
    }
  };

  const readFileAsBuffer = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  };

  const handleCloseViewer = () => {
      setIsViewerOpen(false);
      setTimeout(() => setViewingFile(null), 300);
  };

  const handleFileAction = async (action, file) => {
    try {
      switch (action) {
        case 'view':
          setViewingFile(file);
          setIsViewerOpen(true);
          break;
          
        case 'download':
          await getFile(documentData.id, file.id, file.name);
          break;
          
        case 'toggle_public':
          await setFilePublic(documentData.id, file.id, !file.is_public);
          //toast.success(file.is_public ? 'Файл приховано' : 'Файл опубліковано');
          break;
          
        case 'rename':
          await handleRenameFile(file);
          break;
          
        case 'trash':
          await fileToTrash(documentData.id, file.id);
          //toast.success('Файл відправлено до кошику');
          break;
          
        case 'restore':
          await fileRestoreTrash(documentData.id, file.id);
          //toast.success('Файл поновлено');
          break;
          
        case 'delete':
          if (window.confirm('Ви впевнені, що хочете видалити цей файл?')) {
            await fileRemove(documentData.id, file.id);
            //toast.success('Файл видалено');
          }
          break;
          
        default:
          break;
      }
      
      // Обновляем список файлов после действия
      if (action !== 'view') {
        await loadDocumentFiles();
      }
      
    } catch (error) {
      console.error('Error performing file action:', error);
      //toast.error('Помилка виконання дії');
    }
    
    setActiveFileMenu(null);
  };

  const getFileIcon = (type) => {
    switch (type) {
      case 'pdf': return faFilePdf;
      case 'docx': case 'doc': return faFileWord;
      case 'xlsx': case 'xls': return faFileExcel;
      case 'jpg': case 'png': case 'image': return faImage;
      default: return faFile;
    }
  };

  const getFileColor = (type) => {
    switch (type) {
      case 'pdf': return '#f40f02';
      case 'docx': case 'doc': return '#295b9f';
      case 'xlsx': case 'xls': return '#217346';
      case 'jpg': case 'png': case 'image': return '#e91e63';
      default: return '#6c757d';
    }
  };

  const FileActionsMenu = ({ file, onClose, isTrash }) => {
    const actions = isTrash 
      ? [
          { id: 'restore', label: 'Поновити', icon: faBoxOpen },
          { id: 'delete', label: 'Видалити', icon: faTrash }
        ]
      : [
          { id: 'view', label: 'Перегляд', icon: faEye }, // Додано перегляд
          { id: 'download', label: 'Завантажити', icon: faDownload },
          { id: 'toggle_public', label: file.is_public ? 'Приховати' : 'Опублікувати', icon: file.is_public ? faEyeSlash : faEye },
          { id: 'rename', label: 'Перейменувати', icon: faEdit },
          { id: 'trash', label: 'Відправити до кошику', icon: faTrash }
        ];

    return (
      <div className="actions-dropdown" style={{ minWidth: '200px' }}>
        {actions.map((action) => (
          <button
            key={action.id}
            className="dropdown-item d-flex align-items-center gap-2 p-2"
            onClick={() => handleFileAction(action.id, file)}
            style={{
              border: 'none',
              backgroundColor: 'transparent',
              width: '100%',
              textAlign: 'left',
              fontSize: '14px',
              cursor: 'pointer'
            }}
          >
            <FontAwesomeIcon icon={action.icon} style={{ width: '16px', color: '#6c757d' }} />
            <span style={{ color: '#212529' }}>{action.label}</span>
          </button>
        ))}
        <div style={{ borderTop: '1px solid #dee2e6', padding: '8px' }}>
          <button
            className="btn btn-sm btn-outline-secondary w-100"
            onClick={onClose}
            style={{ fontSize: '12px' }}
          >
            Скасувати
          </button>
        </div>
      </div>
    );
  };

  const InlineEditInput = ({ file, onSave, onCancel }) => {
    const [editValue, setEditValue] = useState(file.name);
    const fileExtension = file.name.split('.').pop();
    const fileNameWithoutExtension = file.name.substring(0, file.name.lastIndexOf('.'));

    useEffect(() => {
      setEditValue(fileNameWithoutExtension);
    }, [file.name]);

    const handleSubmit = (e) => {
      e.preventDefault();
      if (editValue.trim() && editValue !== fileNameWithoutExtension) {
        onSave(`${editValue}.${fileExtension}`);
      } else {
        onCancel();
      }
    };

    return (
      <form onSubmit={handleSubmit} className="d-flex align-items-center gap-2">
        <input
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          className="form-control form-control-sm"
          autoFocus
          onBlur={handleSubmit}
        />
        <button type="submit" className="btn btn-sm btn-success">✓</button>
        <button type="button" className="btn btn-sm btn-secondary" onClick={onCancel}>✕</button>
      </form>
    );
};

const activeFiles = files.filter(f => f.status === 'active');
  const trashFiles = files.filter(f => f.status === 'trash');

  return (
    <>
    <Modal show={isOpen} onHide={onClose} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>
          {documentData ? 'Редагувати документ' : 'Створити новий документ'}
        </Modal.Title>
      </Modal.Header>
      
      <Tabs activeKey={activeTab} onSelect={setActiveTab} className="mb-3">
        <Tab eventKey="details" title="Деталі документа">
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              <Form.Group className="mb-3">
                <Form.Label>Назва*</Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="Введіть назву документа"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Найменування документа: </Form.Label>
                <span style={{ paddingLeft: "10px"}}>{typeDoc?.transl || formData.type}</span>
                {false && <Form.Select
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  required
                > 
                <option value="">Оберіть тип</option>
                  { dictionary.map(d=>(<option value={d.value} >{d.value}</option>)) } 
                  
                </Form.Select>}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Додатковий опис: </Form.Label>
                <span style={{ paddingLeft: "10px"}}>{formData.description}</span>
                {false && <Form.Control
                  as="textarea"
                  rows={3}
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Введіть опис документа"
                />}
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={onClose}>
                Скасувати
              </Button>
              <Button variant="primary" type="submit" disabled={loading}>
                {loading ? 'Збереження...' : (documentData ? 'Зберегти зміни' : 'Додати документ')}
              </Button>
            </Modal.Footer>
          </Form>
        </Tab>

        {documentData && (
          <Tab eventKey="files" title="Файли">
            <Modal.Body>
              <div className="mb-4">
                <FileUploader onUpload={handleFileUpload} disabled={loading} />
              </div>

              {loading ? (
                <div className="text-center p-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Завантаження...</span>
                  </div>
                </div>
              ) : (
                <Tabs activeKey={filesTab} onSelect={setFilesTab} className="mb-3">
                  <Tab eventKey="active" title="Файли документів та фото">
                    <Table striped bordered hover>
                      <thead>
                        <tr>
                          <th>Найменування документу</th>
                          <th>Тип файлу</th>
                          <th>Розмір</th>
                          <th>Дата завантаження</th>
                          <th>Статус</th>
                          <th>Дії</th>
                        </tr>
                      </thead>
                      <tbody>
                        {activeFiles.map((file) => (
                          <tr key={file.id}>
                            <td>
                                {editingFileId === file.id ? (
                                  <InlineEditInput
                                    file={file}
                                    onSave={async (newName) => {
                                      try {
                                        await renameFile(documentData.id, file.id, newName);
                                        //toast.success('Файл перейменовано');
                                        setEditingFileId(null);
                                        await loadDocumentFiles();
                                      } catch (error) {
                                        console.error('Error renaming file:', error);
                                        //toast.error('Помилка перейменування файлу');
                                      }
                                    }}
                                    onCancel={() => setEditingFileId(null)}
                                  />
                                ) : (
                                  <>
                                    <FontAwesomeIcon 
                                      icon={getFileIcon(file.type)} 
                                      className="me-2"
                                      style={{ color: getFileColor(file.type) }}
                                    />
                                    <span 
                                      style={{ cursor: 'pointer' }}
                                      onClick={() => setEditingFileId(file.id)}
                                      title="Натисніть для редагування назви"
                                    >
                                      {file.name}
                                    </span>
                                  </>
                                )}
                              </td>
                            <td>{file.mtype || file.type}</td>
                            <td>{file.size}</td>
                            <td>{file.uploadedAt}</td>
                            <td>
                              <span className={`badge ${file.is_public ? 'bg-success' : 'bg-secondary'}`}>
                                {file.is_public ? 'Публічний' : 'Приватний'}
                              </span>
                            </td>
                            <td>
                              <div className="actions-menu-container" style={{ position: 'relative' }}>
                                <button
                                  className="btn btn-sm btn-link p-0"
                                  onClick={() => setActiveFileMenu(activeFileMenu === file.id ? null : file.id)}
                                  disabled={loading}
                                >
                                  <FontAwesomeIcon icon={faEllipsisVertical} />
                                </button>
                                {activeFileMenu === file.id && (
                                  <FileActionsMenu 
                                    file={file}
                                    onClose={() => setActiveFileMenu(null)}
                                    isTrash={false}
                                  />
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                        {activeFiles.length === 0 && (
                          <tr>
                            <td colSpan="6" className="text-center text-muted">
                              Немає активних файлів
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </Tab>

                  <Tab eventKey="trash" title="кошик">
                    <Table striped bordered hover>
                      <thead>
                        <tr>
                          <th>Назва</th>
                          <th>Тип файлу</th>
                          <th>Розмір файлу</th>
                          <th>Дата завантаження</th>
                          <th>Дії</th>
                        </tr>
                      </thead>
                      <tbody>
                        {trashFiles.map((file) => (
                          <tr key={file.id}>
                            <td>
                              <FontAwesomeIcon 
                                icon={getFileIcon(file.type)} 
                                className="me-2"
                                style={{ color: getFileColor(file.type) }}
                              />
                              {file.name}
                            </td>
                            <td>{file.mtype || file.type}</td>
                            <td>{file.size}</td>
                            <td>{file.uploadedAt}</td>
                            <td>
                              <div className="actions-menu-container" style={{ position: 'relative' }}>
                                <button
                                  className="btn btn-sm btn-link p-0"
                                  onClick={() => setActiveFileMenu(activeFileMenu === file.id ? null : file.id)}
                                  disabled={loading}
                                >
                                  <FontAwesomeIcon icon={faEllipsisVertical} />
                                </button>
                                {activeFileMenu === file.id && (
                                  <FileActionsMenu 
                                    file={file}
                                    onClose={() => setActiveFileMenu(null)}
                                    isTrash={true}
                                  />
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                        {trashFiles.length === 0 && (
                          <tr>
                            <td colSpan="5" className="text-center text-muted">
                              кошик порожний
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </Tab>
                </Tabs>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={onClose}>
                Закрити
              </Button>
            </Modal.Footer>
          </Tab>
        )}
      </Tabs>
    </Modal>
    {isViewerOpen && (
      <FileViewer
        isOpen={isViewerOpen}
        onClose={handleCloseViewer}
        file={viewingFile}
        documentId={documentData?.id}
      />
  )}
</>
  );
};

export default DocumentModal;